import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { PrismicDocument } from '@prismicio/client';
import { format, getPercentageChange } from '@monofront/business/utils';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  IShoppingCartProductBundleInfo,
} from '@monofront/types';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Tag from '@monofront/design-system/components/Tag';
import useProductBasketToShoppingCart from '@hooks/cart/useProductBasketToShoppingCart';
import ButtonAddBasketToCart from '@components/ButtonAddBasketToCart';

interface ICardProductBasketProps {
  productBasket: PrismicDocument;
  containerContext?: AddToShoppingCartContext;
  trackingId?: string;
}

const CardProductBasket: React.FC<ICardProductBasketProps> = ({
  productBasket,
  containerContext = { source: AddToShoppingCartSource.BUNDLE },
  trackingId,
}) => {
  const tCatalog = useTranslations('Catalog');
  const bundleInfos: IShoppingCartProductBundleInfo = {
    uid: productBasket.uid || '',
    image: productBasket.data?.image?.url || '',
    name: productBasket.data?.title?.[0]?.text || '',
    category: productBasket.tags?.[0],
  };

  const { globalPrice, shouldHidePrice } = useProductBasketToShoppingCart(
    productBasket.data?.body?.[0]?.items,
    bundleInfos,
    containerContext,
  );

  const discountPercent = getPercentageChange(
    globalPrice.comparedAtPrice,
    globalPrice.price,
  ).toFixed(0);

  if (
    !productBasket.data ||
    !productBasket.data?.image ||
    !productBasket.data?.title ||
    !productBasket.data?.body?.[0]?.items
  )
    return null;

  return (
    <>
      <div className="card-product-basket container">
        <Link
          href={`/product-basket/${productBasket.uid}${
            trackingId ? `?trackingId=${trackingId}` : ''
          }`}
          legacyBehavior
        >
          <a data-testid="CardProductBasket-go-to-product-basket-0">
            <figure>
              <ResponsiveImage
                src={productBasket.data.image.url}
                alt={productBasket.data.title[0].text}
                width={1024}
                height={1024}
              />
            </figure>
          </a>
        </Link>
        <div className="infos">
          <Link
            href={`/product-basket/${productBasket.uid}${
              trackingId ? `?trackingId=${trackingId}` : ''
            }`}
            legacyBehavior
          >
            <a
              className="title"
              data-testid="CardProductBasket-go-to-product-basket-1"
            >
              {productBasket.data.title[0].text}
            </a>
          </Link>
          <div className="cta">
            <div className="container-price">
              {!shouldHidePrice ? (
                <span className="price">{format(globalPrice.price)}</span>
              ) : (
                <span className="tw-lf-link-md">
                  {tCatalog('seeMemberPrices')}
                </span>
              )}
              <div className="compared-discount">
                {!shouldHidePrice && discountPercent ? (
                  <>
                    <Tag variant="secondary">{`-${discountPercent}%`}</Tag>
                    <span className="compared-price">
                      {format(globalPrice.comparedAtPrice)}
                    </span>
                  </>
                ) : null}
                {productBasket.data.compareAtPrice &&
                productBasket.data.compareAtPrice > 0 ? (
                  <span className="compared-price">
                    {format(globalPrice.comparedAtPrice)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="btn-add">
              <ButtonAddBasketToCart
                hasEditViewOnly={false}
                products={productBasket.data.body[0].items}
                bundleInfos={bundleInfos}
                containerContext={containerContext}
              />
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        figure {
          cursor: pointer;
          height: 100%;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        figure > :global(img) {
          object-fit: cover;
          height: 100%;
        }

        .infos {
          padding: var(--space-3);
        }

        .cta {
          display: flex;
          align-items: center;
          position: relative;
        }

        .container-price {
          width: 50%;
        }

        .title {
          display: block;
          margin-bottom: var(--space-4);
          font-size: var(--font-size-p);
          line-height: 1.5;
          text-overflow: ellipsis;
          cursor: pointer;
        }

        .price {
          font-size: var(--font-size-3);
          font-family: var(--font-family-1);
        }

        .compared-price {
          margin-left: var(--space-1);
          text-decoration: line-through;
          font-size: var(--font-size-1);
          color: var(--clr-grey-1);
        }

        .btn-add {
          position: absolute;
          right: 0;
        }

        .compared-discount {
          display: flex;
          align-items: center;
        }

        .compared-discount > :global(div) {
          font-size: var(--font-size-1);
        }
      `}</style>
    </>
  );
};

export default CardProductBasket;
